import BusinessIcon from '@mui/icons-material/Business';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import WidgetsIcon from '@mui/icons-material/Widgets';
import { Stack } from '@mui/material';
import {
  useCreditLineAssignations,
  useCreditLineForGiver,
} from 'lib/api/creditLine';
import { CreditLineAssignationsResponse } from 'lib/api/creditLine/types';
import { formatDateString, formatMoney } from 'lib/functions/utils';
import {
  ContentCard,
  ErrorState,
  KeyValueCard,
  MobileCaption,
  StatNumber,
  TabContentSkeleton,
} from 'lib/mobileDesignSystem';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useCreditLineOperationDetail } from '../context';

export interface OverviewProps {
  requestId: string;
}

const Overview: React.FC<OverviewProps> = ({ requestId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setOperationStatus } = useCreditLineOperationDetail();

  const requestIdNum =
    requestId != null && requestId !== '' ? Number(requestId) : 0;

  const {
    data: operation,
    isLoading,
    isError,
  } = useCreditLineForGiver(dispatch, requestIdNum);

  const { data: rawAssignations, isLoading: isLoadingAssignations } =
    useCreditLineAssignations(dispatch, requestIdNum);

  useEffect(() => {
    if (operation?.status != null) {
      setOperationStatus(operation.status);
    }
  }, [operation?.status, setOperationStatus]);

  if (isLoading) return <TabContentSkeleton />;

  if (isError || !operation)
    return <ErrorState message="Ocurrió un error al cargar el detalle." />;

  const assignations = rawAssignations as
    | CreditLineAssignationsResponse
    | undefined;

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <ContentCard
        header={{
          icon: <BusinessIcon fontSize="small" />,
          title: operation.businessName,
          description: operation.businessIdentifier,
        }}
      >
        <Stack spacing={1} direction="row">
          <StatNumber
            label="Monto"
            amount={formatMoney(operation.amount)}
            tag="CLP"
          />
        </Stack>
      </ContentCard>

      <ContentCard
        header={{
          icon: <CreditScoreIcon sx={{ fontSize: 20 }} />,
          title: 'Información general',
          description: operation.productName,
        }}
      >
        <KeyValueCard
          items={[
            { label: 'Producto', value: operation.productName },
            {
              label: 'Fecha de creación',
              value: formatDateString(operation.createdAt, 'dd-mm-yyyy'),
            },
            ...(operation.validUntil
              ? [
                  {
                    label: 'Válida hasta',
                    value: formatDateString(operation.validUntil, 'dd-mm-yyyy'),
                  },
                ]
              : []),
          ]}
          highlightLastRows={0}
        />
      </ContentCard>

      {operation.observations ? (
        <ContentCard
          header={{
            icon: <DriveFileRenameOutlineIcon sx={{ fontSize: 20 }} />,
            title: 'Observaciones',
          }}
        >
          <MobileCaption
            component="span"
            sx={{
              color: 'text.primary',
              fontSize: '0.75rem',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {operation.observations}
          </MobileCaption>
        </ContentCard>
      ) : null}

      {!isLoadingAssignations && assignations != null && (
        <>
          <ContentCard
            header={{
              icon: <WidgetsIcon sx={{ fontSize: 20 }} />,
              title: 'Concentraciones',
              description: 'Subproductos y Deudor',
              clickable: true,
              onClick: () =>
                navigate(`/finance/credit-line/${requestId}/sub-products`),
            }}
          />
        </>
      )}
    </Stack>
  );
};

export default Overview;
